/**
 * affine2d — 2×3 affine transform used to compose INSERT / nested-block
 * transforms over TYPED drawing-model entities for the PDF exporter.
 *
 * A matrix `M = [a, b, c, d, e, f]` maps a local point (x, y) to world:
 *   worldX = a·x + c·y + e
 *   worldY = b·x + d·y + f
 *
 * This is the standard 2D affine (column-vector) form. We carry the full 2×3
 * (NOT a scalar radius collapse) so non-uniform scale turns a circle into an
 * ELLIPSE and a mirror (negative determinant) flips arc orientation —
 * `PLAN-plot-pdf.md` §7.7 pitfall #1.
 *
 * AutoCAD INSERT order is scale → rotate → translate, with the block base
 * point subtracted first. We build that here as a composed matrix so nested
 * INSERTs simply multiply matrices.
 */

import type { Point2 } from "../cadPrimitive";

/** A 2×3 affine `[a, b, c, d, e, f]`. */
export interface Affine2D {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/** Identity transform. */
export const IDENTITY: Affine2D = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** Apply `m` to a point. */
export function apply(m: Affine2D, p: Point2): Point2 {
  return {
    x: m.a * p.x + m.c * p.y + m.e,
    y: m.b * p.x + m.d * p.y + m.f,
  };
}

/**
 * Apply only the linear (2×2) part of `m` to a vector/derivative (no
 * translation). Used to transform curve tangents/derivatives.
 */
export function applyLinear(m: Affine2D, v: Point2): Point2 {
  return {
    x: m.a * v.x + m.c * v.y,
    y: m.b * v.x + m.d * v.y,
  };
}

/**
 * Compose two transforms: `compose(outer, inner)` applies `inner` first then
 * `outer` — i.e. `apply(compose(outer, inner), p) === apply(outer, apply(inner, p))`.
 */
export function compose(outer: Affine2D, inner: Affine2D): Affine2D {
  return {
    a: outer.a * inner.a + outer.c * inner.b,
    b: outer.b * inner.a + outer.d * inner.b,
    c: outer.a * inner.c + outer.c * inner.d,
    d: outer.b * inner.c + outer.d * inner.d,
    e: outer.a * inner.e + outer.c * inner.f + outer.e,
    f: outer.b * inner.e + outer.d * inner.f + outer.f,
  };
}

/**
 * Build the INSERT transform matrix per AutoCAD semantics:
 *   1. subtract the block base point  (T(−base))
 *   2. scale (sx, sy)                 (S)
 *   3. rotate (rotationDeg, CCW)      (R)
 *   4. translate to insertion point   (T(insertion))
 * Composed world = T(insertion) · R · S · T(−base).
 *
 * Negative scale components (mirror) are preserved verbatim in the matrix so
 * the determinant sign flips arc orientation downstream.
 */
export function insertMatrix(
  insertion: Point2,
  scaleX: number,
  scaleY: number,
  rotationDeg: number,
  base: Point2,
): Affine2D {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Linear part = R · S
  const a = cos * scaleX;
  const b = sin * scaleX;
  const c = -sin * scaleY;
  const d = cos * scaleY;
  // Translation = insertion − (R·S)·base
  const e = insertion.x - (a * base.x + c * base.y);
  const f = insertion.y - (b * base.x + d * base.y);
  return { a, b, c, d, e, f };
}

/** Signed determinant of the linear part (negative = mirrored). */
export function determinant(m: Affine2D): number {
  return m.a * m.d - m.b * m.c;
}

/** Rotation angle (radians) of the linear part's X-axis image. */
export function rotationOf(m: Affine2D): number {
  return Math.atan2(m.b, m.a);
}

/** Average uniform scale magnitude (geometric mean of axis lengths). */
export function uniformScale(m: Affine2D): number {
  const sx = Math.hypot(m.a, m.b);
  const sy = Math.hypot(m.c, m.d);
  return Math.sqrt(Math.abs(sx * sy));
}
